<script setup>
import { useGetEventItem } from '@/core/composables/useGetEventItem'

const props = defineProps({
  initialEvent: {
    type: Object,
  },
  initialEventId: {
    type: Number,
  },
})

const emit = defineEmits(['show', 'hide'])

const getEventItem = useGetEventItem()

const { routePath } = useRoutePath()
const api = useApi()
const loading = ref(false)

const selectedCustomer = useSelectedCustomer()

const event = ref()
const dayjs = useDayjs()

const { d, t } = useI18n()
const { r } = useRoutes()

const eventDateAndTime = computed(
  () => {
    const startDateTime = event.value?.starts_at ? `${d(event.value.starts_at.slice(0, 19), 'long')} -` : ''
    const endDateTime = event.value?.ends_at ? d(event.value.ends_at.slice(0, 19), 'time') : ''

    return `${startDateTime} ${endDateTime}`
  },
)

const hasInstructors = computed(
  () => event.value?.instructors?.length > 0,
)

const modal = ref(undefined)
const isOpen = ref(false)
const activeOption = ref(1)

const showEvent = ref(true)
const showNoEventFound = ref(false)

async function getEvent(force) {
  if (props.initialEvent && !force) {
    event.value = props.initialEvent
    return
  }

  loading.value = true
  const [eventData, attendanceData] = await Promise.all([
    api.retrieveEvent(props.initialEventId, {
      queryParameters: {
        ...(selectedCustomer.value && { active_customer: selectedCustomer.value }),
      },
      expand: ['venue', 'active_registration_status', 'instructors', 'offering_type'],
    }),
    ...(selectedCustomer.value
      ? [api.listAttendances(
          {
            queryParameters: {
              event: props.initialEventId,
              customer: selectedCustomer.value,
            },
            expand: ['waitlist_spot', 'waitlist_spot.position'],
          },
        )]
      : []),
  ])

  if (eventData) {
    const eventItem = getEventItem({ event: eventData, attendance: attendanceData?.results?.[0], venue: eventData.venue, offeringType: eventData.offering_type })
    event.value = eventItem
  }
  else {
    showNoEventFound.value = true
  }

  loading.value = false
}

function chooseOption(value) {
  activeOption.value = value
}
function show() {
  modal.value.show()
}
function hide() {
  modal.value.hide()
}
function onShow() {
  isOpen.value = true
  emit('show')

  getEvent()
}

async function redirectToLoginPage() {
  const next = encodeURIComponent(`${routePath.value}?e=${props.initialEventId}`)

  hide()
  window.location.href = r({
    path: `/start/?next=${next}`,
    isV2Path: false,
  })
}

async function onHide() {
  isOpen.value = false

  showNoEventFound.value = false
  event.value = undefined
  emit('hide')
}

function toggleEvent() {
  showEvent.value = !showEvent.value
}

defineExpose({
  show,
  hide,
})
</script>

<template>
  <DSModal
    ref="modal"
    :loading="loading"
    modal-class="lt-sm:(w-358px) sm:(w-800px) flex flex-col !rounded-8px bg-white"
    class="overflow-y-auto"
    @show="onShow"
    @hide="onHide"
  >
    <BookingModalNoEventFound
      v-if="showNoEventFound"
      @redirect-to-login="redirectToLoginPage"
    />
    <template v-else>
      <div class="modal__header h-fit p-4">
        <header>
          <div class="title flex flex-col flex-gap-1 pr-4">
            <p class="text-lg text-text-600 font-semibold">
              {{ event?.name }}
            </p>
            <div class="flex justify-between sm:(flex-row items-center) lt-sm:(flex-col)">
              <p class="font-sm text-text-400">
                {{ eventDateAndTime }}
              </p>
              <p class="font-sm text-text-400">
                <TimeZoneInfo :timezones="[event.venue.timezone]" />
              </p>
            </div>
          </div>
        </header>
      </div>
      <hr>
      <div
        class="flex items-center justify-between flex-gap-x-2 p-4 sm:hidden"
      >
        <p class="text-text-600 font-semibold">
          {{ t('Event Details') }}
        </p>
        <button
          class="btn-xs w-77px sm:hidden btn-outline-secondary"
          @click="toggleEvent"
        >
          <span class="uppercase"> {{ showEvent ? t('Hide') : t('Show') }} </span>
        </button>
      </div>
      <div
        class="modal__body flex flex-gap-4 p-4 lt-sm:(mb-4 flex-col overflow-y-auto py-0) sm:h-400px"
      >
        <div
          class="modal__tab-container flex basis-full flex-col flex-gap-y-4"
          :class="{ 'lt-sm:hidden': !showEvent }"
        >
          <div class="modal__tab-bar">
            <BookingModalTabBar
              v-model="activeOption"
              :has-instructors="hasInstructors"
              @option="chooseOption"
            />
          </div>
          <div
            class="modal__tab-content flex-grow b-1 border-surface-600 rounded-4px bg-surface-100 sm:overflow-y-auto"
          >
            <BookingModalOverviewTab
              v-if="activeOption === 1"
              :event="event"
            />
            <BookingModalVenueTab
              v-if="activeOption === 2"
              :venue="event?.venue"
            />
            <BookingModalInstructorTab
              v-if="hasInstructors && activeOption === 3"
              :instructors="event?.instructors"
            />
          </div>
        </div>
        <!-- Booking Modal section -->
        <div class="modal__registration basis-full overflow-hidden">
          <BookingModalBookingSection
            v-if="event"
            :key="event?.id"
            :event="event"
            :hide="hide"
            @update="getEvent(true)"
            @close="hide"
            @redirect-to-login="redirectToLoginPage"
          />
        </div>
      </div>
    </template>
  </DSModal>
</template>

<i18n>
{
  "en-us": {
    "Welcome to {provider}": "Welcome to {provider}",
    "Enter your email to get started": "Enter your email to get started",
    "Email": "Email",
    "Next": "Next",
    "Event Details": "Event Details"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Welcome to {provider}": "Willkommen bei {provider}",
  "Enter your email to get started": "Geben Sie Ihre E-Mail-Adresse ein, um loszulegen",
  "Email": "E-Mail",
  "Next": "Weiter",
  "Event Details": "Details zum Ereignis"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Welcome to {provider}": "Bienvenue chez {provider}",
  "Enter your email to get started": "Saisissez votre email pour vous lancer",
  "Email": "E-mail",
  "Next": "Suivant",
  "Event Details": "Détails de l'événement"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Welcome to {provider}": "Le damos la bienvenida a {provider}",
  "Enter your email to get started": "Introduzca su correo electrónico para comenzar",
  "Email": "Correo electrónico",
  "Next": "Siguiente",
  "Event Details": "Detalles de evento"
}
</i18n>
